import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { isDefined, isNotDefined } from '@sgme/fp';
import deepEqual from 'fast-deep-equal';
import PQueue from 'p-queue';
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';

import { useAppSelector } from '@/store/hooks.ts';
import type { ValidateOrderResponse } from '@/store/orders/state.ts';
import { useValidateStopLossMutation } from '@/store/orders/stopLoss/api/index.ts';
import { selectStopLossTile } from '@/store/orders/stopLoss/selectors.ts';
import type { StopLossOrder } from '@/store/orders/stopLoss/state.ts';
import { selectTileClientId } from '@/store/tiles/selectors.ts';
import { normalizeEmailsList } from '@/utils/notificationEmails.ts';

export type StopLossContextContent = {
  tileId: string;
  isChecking: boolean;
  isModifyingField: boolean;
  setIsModifyingField: (isModifying: boolean) => void;
  validateStopLoss: (updatedProperties: Partial<StopLossOrder['properties']>) => Promise<
    | {
        data: ValidateOrderResponse;
        error: undefined;
      }
    | {
        data: undefined;
        error: FetchBaseQueryError | SerializedError;
      }
    | undefined
  >;
};

type StopLossValidateFn = StopLossContextContent['validateStopLoss'];

type StopLossModifyingFieldContextContent = {
  isModifyingField: boolean;
  setIsModifyingField: (isModifying: boolean) => void;
};

const StopLossTileIdContext = createContext('');
const StopLossIsCheckingContext = createContext(false);
const StopLossValidateContext = createContext<StopLossValidateFn>(async (_updatedProperties) => undefined);
const StopLossModifyingFieldContext = createContext<StopLossModifyingFieldContextContent>({
  isModifyingField: false,
  setIsModifyingField: () => {},
});

export const StopLossContext = createContext<StopLossContextContent>({
  tileId: '',
  isChecking: false,
  validateStopLoss: async (_updatedProperties: Partial<StopLossOrder['properties']>) => undefined,
  isModifyingField: false,
  setIsModifyingField: () => {},
});

export const StopLossProvider = (props: { tileId: string; children: ReactNode }) => {
  const [isChecking, setIsChecking] = useState(false);
  const [isModifyingField, setIsModifyingField] = useState(false);

  const { children, tileId } = props;
  const [validateStopLossMutation] = useValidateStopLossMutation({
    selectFromResult: () => ({}),
  });
  const clientId = useAppSelector((state) => selectTileClientId(state, tileId));

  const { tile, preferencesNotificationEmails } = useAppSelector((state) => {
    const userPreferences = state.userPreferences;

    if (isNotDefined(userPreferences)) {
      console.error('[useValidateStopLoss] User preferences not found in state.');
      return {};
    }

    const tile = selectStopLossTile(state, tileId);

    if (isNotDefined(tile)) {
      console.error(`[useValidateStopLoss] Tile with id ${tileId} not found in state.`);
      return {};
    }

    if (tile.type !== 'StopLossOrder') {
      console.error(`[useValidateStopLoss] Tile with id ${tileId} is not a StopLossOrder.`);
      return {};
    }

    return {
      tile,
      preferencesNotificationEmails: userPreferences.splitNotifications
        ? userPreferences.splitNotificationsEmailsOrders
        : userPreferences.emails,
    };
  }, deepEqual);

  // Queue with concurrency=1 to ensure sequential execution
  // Only one handleValidateStopLoss request can run at a time
  const queue = useMemo(() => new PQueue({ concurrency: 1 }), []);

  const handleValidateStopLoss = useCallback(
    async (updatedProperties: Partial<StopLossOrder['properties']>) => {
      return queue.add(async () => {
        if (isNotDefined(tile)) {
          console.error(`[handleValidateStopLoss] Tile with id ${tileId} not found in state.`);
          return;
        }

        const currency1 = updatedProperties.currencyPair?.currency1 ?? tile.properties.currencyPair?.currency1;
        const currency2 = updatedProperties.currencyPair?.currency2 ?? tile.properties.currencyPair?.currency2;

        if (isNotDefined(clientId)) {
          console.error('[handleValidateStopLoss] client id is not defined');
          return;
        }

        if (isNotDefined(currency1) || isNotDefined(currency2)) {
          console.error(`[handleValidateStopLoss] Invalid currency pair: ${currency1}/${currency2}`);
          return;
        }

        const updatedStopLoss = {
          ...tile,
          properties: {
            ...tile.properties,
            ...updatedProperties,
          },
        };

        const patchedKeys = Object.keys(updatedProperties);
        const keysWhichShouldMakeLimitPriceReset = ['currencyPair', 'clientWay'];
        const shouldResetLimitPrice = keysWhichShouldMakeLimitPriceReset.some((key) => patchedKeys.includes(key));

        const notificationEmails = normalizeEmailsList(
          updatedStopLoss.properties.notificationEmails ?? preferencesNotificationEmails ?? [],
        );

        const checkTimer = setTimeout(() => {
          setIsChecking(true);
        }, 500);

        const data = await validateStopLossMutation({
          tileId,
          currency1,
          currency2,
          clientId,
          way: updatedStopLoss.properties.clientWay,
          notional: updatedStopLoss.properties.notional,
          trigger: updatedStopLoss.properties.trigger,
          notionalCurrencyIndex: updatedStopLoss.properties.notionalCurrencyIndex,
          limitPrice: !shouldResetLimitPrice ? updatedStopLoss.properties.limitPrice : undefined,
          expiry: updatedStopLoss.properties.expiry,
          notificationEmails: {
            isOverridenEmails: isDefined(updatedProperties.notificationEmails),
            emails: notificationEmails,
          },
          clockOffset: updatedStopLoss.properties.clockOffset,
        });

        clearTimeout(checkTimer);
        setIsChecking(false);
        return data;
      });
    },
    [tile, preferencesNotificationEmails, tileId, validateStopLossMutation, clientId, queue],
  );

  const validateStopLoss = useCallback<StopLossValidateFn>(
    async (updatedProperties) => {
      const result = await handleValidateStopLoss(updatedProperties);

      if (isNotDefined(result)) {
        console.error('[validate] Validation result is undefined');
        return undefined;
      }

      if ('data' in result && result.data !== undefined) {
        return { data: result.data, error: undefined };
      }

      if ('error' in result && result.error !== undefined) {
        return { data: undefined, error: result.error };
      }

      return undefined;
    },
    [handleValidateStopLoss],
  );

  const modifyingFieldContent = useMemo(
    () => ({
      isModifyingField,
      setIsModifyingField,
    }),
    [isModifyingField],
  );

  const content = useMemo(
    () => ({
      tileId,
      isChecking,
      validateStopLoss,
      ...modifyingFieldContent,
    }),
    [isChecking, modifyingFieldContent, tileId, validateStopLoss],
  );

  return (
    <StopLossTileIdContext.Provider value={tileId}>
      <StopLossIsCheckingContext.Provider value={isChecking}>
        <StopLossValidateContext.Provider value={validateStopLoss}>
          <StopLossModifyingFieldContext.Provider value={modifyingFieldContent}>
            <StopLossContext.Provider value={content}>{children}</StopLossContext.Provider>
          </StopLossModifyingFieldContext.Provider>
        </StopLossValidateContext.Provider>
      </StopLossIsCheckingContext.Provider>
    </StopLossTileIdContext.Provider>
  );
};

export function useStopLossTileId() {
  return useContext(StopLossTileIdContext);
}

export function useStopLossIsChecking() {
  return useContext(StopLossIsCheckingContext);
}

export function useValidateStopLoss() {
  return useContext(StopLossValidateContext);
}

export function useStopLossIsModifyingField() {
  return useContext(StopLossModifyingFieldContext).isModifyingField;
}

export function useStopLossSetIsModifyingField() {
  return useContext(StopLossModifyingFieldContext).setIsModifyingField;
}
